import { useRef, useState } from "react";

/**
 * Gráfica SVG de previsualización de tarifa.
 * @param {number} initialRate  - Tarifa base en MXN
 * @param {number} kilometers   - Km cubiertos por la tarifa base
 * @param {number} pricePerKilometer - Precio extra por km adicional
 * @param {boolean} isDynamic   - Si la tarifa dinámica está activa
 * @param {number} priceOnDemand - Precio por demanda (0 = ignorar)
 * @param {number} [highlightKm] - Km a resaltar con un punto fijo (ej. distancia recorrida)
 * @param {number} [width=280]  - Ancho del SVG
 * @param {number} [height=90]  - Alto del SVG
 */
const RatePreviewChart = ({
  initialRate,
  kilometers,
  pricePerKilometer,
  isDynamic,
  priceOnDemand,
  highlightKm,
  actualCost,
  width: w = 280,
  height: h = 90,
}) => {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);

  const baseFare = parseFloat(initialRate) || 0;
  const baseKm = parseFloat(kilometers) || 0;
  const perKm = parseFloat(pricePerKilometer) || 0;
  const demand = parseFloat(priceOnDemand) || 0;
  const showDynamic = isDynamic && perKm > 0;

  if (baseFare === 0 && !showDynamic) return null;

  const maxKm = showDynamic
    ? Math.max(baseKm, highlightKm || 0) + 5
    : Math.max(baseKm, highlightKm || 0, 1);

  const pad = { l: 36, r: 8, t: 8, b: 22 };
  const iw = w - pad.l - pad.r;
  const ih = h - pad.t - pad.b;

  const calcCost = (km) => {
    const extra = showDynamic ? Math.max(0, km - baseKm) * perKm : 0;
    return baseFare + extra + (demand > 0 ? demand : 0);
  };

  const points = [];
  for (let km = 0; km <= maxKm; km += 0.25) points.push({ km, cost: calcCost(km) });
  
  const maxCost = Math.max(
    ...points.map((p) => p.cost),
    baseFare + 1,
    actualCost != null ? parseFloat(actualCost) : 0
  );

  const toX = (km) => pad.l + (km / maxKm) * iw;
  const toY = (c) => pad.t + ih - (c / maxCost) * ih;

  const pathD = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${toX(p.km).toFixed(1)} ${toY(p.cost).toFixed(1)}`)
    .join(" ");
  const fillD = `${pathD} L ${toX(maxKm).toFixed(1)} ${(pad.t + ih).toFixed(1)} L ${toX(0).toFixed(1)} ${(pad.t + ih).toFixed(1)} Z`;

  const kmTicks = Array.from({ length: Math.floor(maxKm) + 1 }, (_, i) => i);

  const handleMouseMove = (e) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const svgX = (e.clientX - rect.left) * (w / rect.width);
    const rawKm = ((svgX - pad.l) / iw) * maxKm;
    if (rawKm < 0 || rawKm > maxKm) { setHover(null); return; }
    const km = Math.round(rawKm * 10) / 10;
    setHover({ x: toX(km), y: toY(calcCost(km)), km, cost: calcCost(km) });
  };

  const hlX = highlightKm != null ? toX(highlightKm) : null;
  const formulaCost = highlightKm != null ? calcCost(highlightKm) : null;
  const hlCost = actualCost != null ? parseFloat(actualCost) : formulaCost;
  const hlY = highlightKm != null && hlCost != null ? toY(hlCost) : null;
  const isDifferent = actualCost != null && formulaCost != null && Math.abs(parseFloat(actualCost) - formulaCost) > 0.5;

  return (
    <div style={{ width: "100%", paddingTop: 6 }}>
      <p style={{ fontSize: 10, color: "#8f8f8f", marginBottom: 4, fontFamily: "Roboto", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px" }}>
        Vista previa de cobro
      </p>
      <svg
        ref={svgRef}
        width={w}
        height={h}
        style={{ display: "block", cursor: "crosshair", maxWidth: "100%" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id="rpcFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#f97316" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
          </linearGradient>
        </defs>

        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line key={f} x1={pad.l} x2={w - pad.r} y1={pad.t + ih * (1 - f)} y2={pad.t + ih * (1 - f)} stroke="#e5e7eb" strokeWidth={0.5} />
        ))}
        {kmTicks.map((km) => (
          <line key={km} x1={toX(km)} x2={toX(km)} y1={pad.t} y2={pad.t + ih} stroke="#f3f4f6" strokeWidth={km === 0 ? 0 : 0.8} />
        ))}
        {showDynamic && baseKm > 0 && (
          <line x1={toX(baseKm)} x2={toX(baseKm)} y1={pad.t} y2={pad.t + ih} stroke="#d1d5db" strokeWidth={1.2} strokeDasharray="4,3" />
        )}

        <path d={fillD} fill="url(#rpcFill)" />
        <path d={pathD} fill="none" stroke="#f97316" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />

        {kmTicks.map((km) => (
          <text key={km} x={toX(km)} y={h - 4} textAnchor="middle" fontSize={7.5} fill="#9ca3af">{km}</text>
        ))}
        <text x={pad.l - 2} y={pad.t + 5} textAnchor="end" fontSize={7} fill="#9ca3af">${maxCost.toFixed(0)}</text>
        <text x={pad.l - 2} y={pad.t + ih} textAnchor="end" fontSize={7} fill="#9ca3af">${baseFare.toFixed(0)}</text>
        <text x={w / 2} y={h - 1} textAnchor="middle" fontSize={7} fill="#c4c4c4">km</text>

        {/* Punto fijo: distancia recorrida */}
        {hlX != null && (
          <g>
            <line x1={hlX} x2={hlX} y1={pad.t} y2={pad.t + ih} stroke="#6366f1" strokeWidth={1.2} strokeDasharray="3,2" />
            <circle cx={hlX} cy={hlY} r={5} fill="#6366f1" stroke="white" strokeWidth={1.5} />
            <rect x={hlX - 34} y={pad.t} width={68} height={14} rx={3} fill="#6366f1" opacity={0.9} />
            <text x={hlX} y={pad.t + 10} textAnchor="middle" fontSize={8} fill="white">
              {highlightKm.toFixed(1)} km — ${hlCost.toFixed(2)}{isDifferent ? " (Total)" : ""}
            </text>
          </g>
        )}

        {/* Punto hover */}
        {hover && !highlightKm && (
          <g>
            <line x1={hover.x} x2={hover.x} y1={pad.t} y2={pad.t + ih} stroke="#f97316" strokeWidth={1} strokeDasharray="3,2" />
            <circle cx={hover.x} cy={hover.y} r={4} fill="#f97316" stroke="white" strokeWidth={1.5} />
            <rect x={hover.x + 6} y={hover.y - 14} width={62} height={15} rx={3} fill="#1a1a1a" opacity={0.85} />
            <text x={hover.x + 9} y={hover.y - 4} fontSize={8} fill="white">
              {hover.km.toFixed(1)} km — ${hover.cost.toFixed(2)}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
};

export default RatePreviewChart;
